import * as Sentry from "@sentry/vue"
import type { App } from "vue"
import type { Router } from "vue-router"
import { isNavigationFailure } from "vue-router"
import errorTypeConst from "@common_lib/base/constants/errorTypeConst"
import { isChunkLoadError, scheduleChunkErrorReload } from "@/utils/chunkLoad"

const IS_DEV = import.meta.env.MODE === "dev" || import.meta.env.MODE === "dev-nginx"
const IS_PROD = import.meta.env.MODE === "prod"

const PROD_CUSTOM_CONFIG = {
  sampleRate: 0.005,
  tracesSampleRate: 0,
  maxBreadcrumbs: 10,
  normalizeDepth: 3
}

const UAT_CUSTOM_CONFIG = {
  sampleRate: 1,
  tracesSampleRate: 1,
  maxBreadcrumbs: 100,
  normalizeDepth: 5
}

const CUSTOM_CONFIG = IS_PROD ? PROD_CUSTOM_CONFIG : UAT_CUSTOM_CONFIG

const IGNORE_ERRORS = [
  "error occurred",
  "request canceled",
  "network error",
  /unable to preload/i,
  "locale messages",
  /the play\(\) request was interrupted/i, // jbl.js media error
  "AxiosError: Request aborted",
  "AxiosError: Network Error",
  // 410 Gone is an expected backend business signal (session / resource gone),
  // handled per-endpoint in api.ts — drop the Sentry noise without changing flow.
  "Request failed with status code 410",
  // Native fetch failures from network drops, page-unload aborts, or third-party scripts.
  // Anchored to avoid masking the chunk-load variant ("Failed to fetch dynamically imported module: ...").
  /^(?:TypeError: )?Failed to fetch$/i,
  "mini game webSocket is not connected",
  /(?:Error invoking \w+: )?Method not found/i, // JBL WebSocket method not found
  "JBLWebSocket is not defined",
  /Can't find variable: JBLWebSocket/i,
  "cordova is not defined",
  /Can't find variable: cordova/i,
  // Third-party browser-injected scripts (Samsung Internet, X Browser, etc.)
  "swbrowser is not defined",
  /Can't find variable: swbrowser/i,
  "xbrowser is not defined",
  /Can't find variable: xbrowser/i,
  // Injected third-party / in-app WebView chart script; `plot` exists nowhere
  // in this app, so the ReferenceError is never ours (onerror, anonymous frame).
  "plot is not defined",
  /Can't find variable: plot/i,
  // Benign browser warnings: ResizeObserver aborts a layout loop without
  // breaking functionality; W3C and Chromium classify these as safe to ignore.
  /ResizeObserver loop limit exceeded/,
  /ResizeObserver loop completed with undelivered notifications/
]

// 建立排除的錯誤條件列表（status code 和 message 都納入比對）
const EXCLUDED_ERROR_MATCHERS = Object.values(errorTypeConst)
  .filter(errorInfo => errorInfo.excludeFromSentry)
  .flatMap(errorInfo => [errorInfo.status, errorInfo.message].filter(Boolean) as string[])

// Status codes of expected business states that must never reach Sentry.
const EXCLUDED_ERROR_STATUSES = new Set(
  Object.values(errorTypeConst)
    .filter(errorInfo => errorInfo.excludeFromSentry)
    .map(errorInfo => String(errorInfo.status))
)

// api.ts rejects some expected business states (system maintenance, IP block,
// session timeout, ...) with the raw response object ({ status, status_msg,
// umTime }) instead of an Error. Sentry serializes those as "Object captured as
// exception/promise rejection with keys: ..." so the status/message never lands
// in event.exception.values[0].value and the string-based EXCLUDED_ERROR_MATCHERS
// can't catch them. Inspect the original rejected object's status field directly.
const isExcludedStatusReject = (value: unknown): boolean => {
  if (typeof value !== "object" || value === null || !("status" in value)) return false
  const { status } = value
  return typeof status === "string" && EXCLUDED_ERROR_STATUSES.has(status)
}

const DENY_URLS = [
  /extensions\//i,
  /^chrome:\/\//i,
  /jbl\.js/i,
  "player-min.js",
  /analysis\.qn32\.com/i,
  /akamaized\.net/i, // Betradar LMT widget external errors
  /freshchat\.com/i, // FreshChat widget JSON parse errors
  /ucbrowser_script\.js/i // UCBrowser injected script errors
]

const errorCounts = new Map()
const ERROR_THRESHOLD = 5

const initSentry = ({ app, router }: { app: App; router: Router }) => {
  if (IS_DEV) return

  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [Sentry.browserTracingIntegration({ router })],
    denyUrls: DENY_URLS,
    ignoreErrors: IGNORE_ERRORS,
    beforeSend(event, hint) {
      // vue-router rejects the navigation promise on next(false) /
      // next({...}) by spec; these are flow-control signals, not bugs.
      if (isNavigationFailure(hint?.originalException)) {
        return null
      }

      // Expected business states rejected as raw objects (system maintenance,
      // IP block, ...) that the message-based filter below can't see.
      // (9WICKET-CLIENT-2P9 / 2P8 / 2PG "System is under Maintenance")
      if (isExcludedStatusReject(hint?.originalException)) {
        return null
      }

      // Instrumented stack-overflow captures (see @/utils/captureStackOverflow)
      // carry a real source label. Always keep them so they survive the
      // phantom-iOS filter below and the dedupe threshold.
      if (event.tags?.stackOverflowSource) return event

      if (!event.exception?.values) return event

      const errorMessage = event.exception.values[0].value

      if (!errorMessage) return event

      // 檢查是否為需要排除的錯誤（比對 status code 和 message）
      const shouldExclude = EXCLUDED_ERROR_MATCHERS.some((matcher: string) =>
        errorMessage.includes(matcher)
      )

      if (shouldExclude) {
        return null
      }

      // Chunk load errors are caused by deploy cache invalidation. Always drop these
      // events: router.onError already schedules a reload, and the same error also
      // reaches here via Vue's errorHandler, where the sessionStorage flag may already
      // be set — checking the reload return value would let the duplicate slip through.
      if (isChunkLoadError(errorMessage)) {
        scheduleChunkErrorReload(errorMessage)
        return null
      }

      // Chrome Mobile iOS reports phantom `Maximum call stack size exceeded` from
      // injected content scripts (translate, password manager, ad blockers) with no
      // source map and a stacktrace of `undefined:N:N`. Persists across releases
      // since 2025-07; unactionable without real frames. Scoped to Chrome iOS so
      // genuine recursion bugs on other browsers still surface.
      const browserName = event.contexts?.browser?.name
      if (
        errorMessage === "Maximum call stack size exceeded." &&
        browserName === "Chrome Mobile iOS"
      ) {
        return null
      }

      const count = (errorCounts.get(errorMessage) || 0) + 1
      errorCounts.set(errorMessage, count)

      if (count > ERROR_THRESHOLD) {
        return null
      }

      return event
    },
    ...CUSTOM_CONFIG
  })
}

export default initSentry
